import aframe from "aframe";
import { bindEvent } from "aframe-event-decorators";
import { randomInt } from "@micosmo/core/number";
import { instantiate } from "@micosmo/aframe/lib/utils";
import gameState from "./game-state";
import * as ticker from '@micosmo/ticker/aframe-ticker';
import warp from "./warp";

const missileCountInterval = 115;
const cityCountInterval = 300;
const manaCountInterval = 300;
const extraCityInterval = 300;
const nextLevelInterval = 2000;

const MissilePoints = 20;
const CityPoints = 100;
const ManaPoints = 50;

const ExtraCityAtScore = 10000;
let extraCities = 0;

aframe.registerComponent("bonus-points", {
  init() {
    this.state = 0;
    this.cityTokensEl = createBonusCityTokens();
    this.missilePointsEl = this.el.querySelector("[text__bonusmissiles]");
    this.cityPointsEl = this.el.querySelector("[text__bonuscities]");
    this.extraCityEl = this.el.querySelector("#extraCity");
    this.bonusManaEl = this.el.querySelector('#bonusMana');
    this.manaPointsEl = this.bonusManaEl.querySelector('[text__bonusmana]');
    this.manaUnitsEl = this.bonusManaEl.querySelector('#manaUnits');
    this.manabarStartPosition = Object.assign({}, this.manaUnitsEl.getAttribute('position'));
  },

  gamestatechanged: bindEvent({ target: "[game-state]" }, function (evt) {
    const newState = evt.detail;
    if (newState === 'Loading') {
      createManaTokens(this); // Can't do this any earlier
      this.missileTokensEl = createBonusMissileTokens();
    }
    if (newState === "LevelComplete") {
      this.el.setAttribute("paused", false);
      hideManaTokens(this);
      this.el.setAttribute("visible", true);
      this.startCounting();
    } else {
      if (this.bonusProcess)
        this.bonusProcess.stop();
      this.el.setAttribute("paused", true);
      this.el.setAttribute("visible", false);
    }
  }),

  startCounting() {
    this.timer = 0;
    this.liveAmmoPiles = Array.from(
      document.querySelectorAll("[missile-silo]")
    ).filter(el => el.getAttribute("visible"));
    this.liveCities = Array.from(document.querySelectorAll(".city")).filter(
      el => !el.components.city.isDead
    );
    this.missilesRemaining = this.liveAmmoPiles.reduce(
      (acc, x) => acc + x.getAttribute("ammo").count,
      0
    );
    this.citiesRemaining = this.liveCities.length;
    this.mana = warp.mana;
    this.manaUnits = this.mana.manaUnits;
    this.isFinishedCounting = false;
    this.missileTokensEl.setAttribute("ammo", "count", 0);
    this.cityTokensEl.setAttribute("ammo", "count", 0);
    this.missilePointsEl.setAttribute("text__bonusmissiles", "value", 0);
    this.cityPointsEl.setAttribute("text__bonuscities", "value", 0);
    this.manaPointsEl.setAttribute("text__bonusmana", "value", 0);
    this.extraCityEl.object3D.visible = false;
    this.bonusManaEl.object3D.visible = this.manaUnits > 0;

    this.totalMissilePoints = 0;
    this.totalCityPoints = 0;
    this.totalManaPoints = 0;
    this.manaUnit = 0;

    const self = this;
    self.bonusProcess = ticker.startProcess({
      name: 'BonusPoints',
      onTick: function * () {
        if (self.missilesRemaining > 0)
          yield ticker.looper(self.missilesRemaining, ticker.msWaiter(missileCountInterval, tickMissileCount.bind(self)));

        if (self.citiesRemaining > 0)
          yield ticker.looper(self.citiesRemaining, ticker.msWaiter(cityCountInterval, tickCityCount.bind(self)));

        if (self.manaUnits > 0)
          yield ticker.looper(self.manaUnits, ticker.msWaiter(manaCountInterval, tickManaCount.bind(self)));

        const score = gameState.score;
        const deadCities = Array.from(document.querySelectorAll(".city")).filter(
          c => c.components.city.isDead
        );
        if (score >= ExtraCityAtScore * (extraCities + 1) && deadCities.length) {
          extraCities += 1;
          const randomCity = deadCities[randomInt(0, deadCities.length - 1)];
          yield ticker.msWaiter(extraCityInterval);
          randomCity.components.city.reset();
          self.extraCityEl.object3D.visible = true;
          self.el.components.misound__citypoint.playSound();
        }
        yield ticker.msWaiter(nextLevelInterval);
        document
          .querySelector("[game-state]")
          .setAttribute("game-state", "state", "StartNextLevel");
      },
      onEnd: () => {
        self.bonusProcess = undefined;
      },
      locateTicker: self.el
    });
  }
});

function tickMissileCount() {
  this.missileTokensEl.setAttribute(
    "ammo",
    "count",
    this.missileTokensEl.getAttribute("ammo").count + 1
  );
  this.totalMissilePoints += MissilePoints * gameState.multiplier;
  this.missilePointsEl.setAttribute(
    "text__bonusmissiles",
    "value",
    this.totalMissilePoints
  );
  this.el.emit("addscore", MissilePoints, true);
  this.el.components.misound__missilepoint.playSound();

  while (
    this.liveAmmoPiles.length > 0 &&
    this.liveAmmoPiles[0].getAttribute("ammo").count === 0
  ) {
    this.liveAmmoPiles.shift();
  }
  if (this.liveAmmoPiles.length > 0) {
    this.liveAmmoPiles[0].setAttribute(
      "ammo",
      "count",
      this.liveAmmoPiles[0].getAttribute("ammo").count - 1
    );
  }
}

function tickCityCount() {
  this.cityTokensEl.setAttribute(
    "ammo",
    "count",
    this.cityTokensEl.getAttribute("ammo").count + 1
  );
  this.totalCityPoints += CityPoints * gameState.multiplier;
  this.cityPointsEl.setAttribute(
    "text__bonuscities",
    "value",
    this.totalCityPoints
  );
  this.el.emit("addscore", CityPoints, true);
  this.el.components.misound__citypoint.playSound();
}

function tickManaCount() {
  this.totalManaPoints += ManaPoints * gameState.multiplier;
  this.manaTokens[this.manaUnit++].setAttribute('visible', true);
  this.manaPointsEl.setAttribute("text__bonusmana", "value", this.totalManaPoints);
  this.el.emit("addscore", ManaPoints, true);
  this.el.components.misound__missilepoint.playSound();
}

function createBonusMissileTokens() {
  const columns = 44;
  const container = document.querySelector("#bonusMissiles");
  const totalMissiles = totalNumberOfMissiles();
  let prefab = `<a-entity ammo="count:${totalMissiles}" position="0.052 -0.004 0">`;
  for (let i = 0; i < totalMissiles; i++) {
    const row = Math.trunc(i / columns);
    const col = i % columns;
    const posX = col * 0.01;
    const posY = -row * 0.01;
    prefab += `<a-entity position="${posX} ${posY} 0" gltf-blender-part="part:missile-token;src:./assets/game-actors/game-actors.gltf"></a-entity>`;
  }
  prefab += "</a-entity>";
  const tokensEl = instantiate(prefab);
  container.appendChild(tokensEl);
  return tokensEl;
}

function createBonusCityTokens() {
  const container = document.querySelector("#bonusCities");
  const totalCities = totalNumberOfCities();
  let prefab = `<a-entity ammo="count:${totalCities};max:${totalCities}" position="0.069 -0.014 0">`;
  for (let i = 0; i < totalCities; i++) {
    const posX = i * 0.05;
    prefab += `<a-entity position="${posX} 0 0" gltf-blender-part="part:city;src:./assets/game-actors/game-actors.gltf" scale="0.5 0.5 0.5" rotation="15 0 0"></a-entity>`;
  }
  prefab += "</a-entity>";
  const tokensEl = instantiate(prefab);
  container.appendChild(tokensEl);
  return tokensEl;
}

function createManaTokens(that) {
  const mana = warp.mana;
  const height = 0.01;
  const bgWidth = mana.manaUnitWidth + 0.004;
  const bgHeight = height + 0.004;
  that.manaTokens = [];
  for (let i = 0, x = bgWidth / 2, prefab; i < mana.manaMaxUnits; i++, x += bgWidth + mana.manaUnitWidth / 3) {
    prefab = `<a-entity position="${x} 0 0">`;
    prefab += `<a-plane color="white" height="${bgHeight}" width="${bgWidth}"></a-plane>`;
    prefab += `<a-plane position="0 0 0.0001" color="green" height="${height}" width="${mana.manaUnitWidth}"></a-plane>`
    prefab += '</a-entity>';
    const el = instantiate(prefab);
    that.manaUnitsEl.appendChild(el);
    that.manaTokens.push(el);
  }
}

function hideManaTokens(that) {
  that.manaTokens.forEach(el => el.setAttribute('visible', false));
}

function totalNumberOfMissiles() {
  return Array.from(document.querySelectorAll("[missile-silo]")).reduce(
    (acc, x) => acc + x.getAttribute("ammo").max,
    0
  );
}

function totalNumberOfCities() {
  return Array.from(document.querySelectorAll("[city]")).length;
}
